import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of, timer } from 'rxjs';
import { catchError, map, mergeMap, timeout } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { resolveAuth0Enabled } from '../../auth/resolve-auth0-enabled';
import {
  SsoLookupResult,
  SsoLookupStatus,
  SsoPreference,
  SsoPreferenceResponse,
  SsoProvider,
  normalizeSsoPreference,
} from './sso-preference.model';

/**
 * Email suffixes / local-parts the mock recognises, so every branch of the
 * email-first flow can be exercised in dev and QA without the real endpoint.
 *
 * These exist ONLY in mock mode and have no effect once
 * `environment.ssoPreference.useMock` is false.
 */
export const MOCK_NOT_FOUND_LOCAL_PARTS = ['notfound', 'unknown', 'nosuchuser'];
export const MOCK_ERROR_LOCAL_PARTS = ['error', 'fail', 'timeout'];
export const MOCK_MICROSOFT_DOMAINS = ['mobiltex.com', 'microsoft.test'];
export const MOCK_ENTERPRISE_DOMAINS = ['okta.test', 'enterprise.test', 'saml.test'];

/**
 * Resolves the mock lookup result for an email address.
 *
 * Pure and synchronous so the branch rules are directly unit-testable; the
 * observable wrapper in {@link SsoPreferenceService} only adds latency.
 */
export function resolveMockPreference(email: string): SsoLookupResult {
  const normalized = email.trim().toLowerCase();
  const atIndex = normalized.lastIndexOf('@');
  const localPart = atIndex >= 0 ? normalized.slice(0, atIndex) : normalized;
  const domain = atIndex >= 0 ? normalized.slice(atIndex + 1) : '';

  if (MOCK_NOT_FOUND_LOCAL_PARTS.includes(localPart)) {
    return { status: SsoLookupStatus.NotFound };
  }

  if (MOCK_ERROR_LOCAL_PARTS.includes(localPart)) {
    return { status: SsoLookupStatus.Error };
  }

  if (MOCK_MICROSOFT_DOMAINS.includes(domain)) {
    return {
      status: SsoLookupStatus.Found,
      preference: {
        ssoEnabled: true,
        provider: SsoProvider.Microsoft,
        // Mirror the real cor-qa endpoint: Microsoft/Azure AD users return
        // saml_connection_name: null, so the hand-off falls back to
        // environment.auth0Config.defaultSsoConnection ('CorView-QA-Auth0-SSO'),
        // which is the connection actually enabled on the cor-qa Auth0 tenant.
        // A hard-coded fake connection here (e.g. 'mock-azure-ad-connection') is
        // rejected by real Auth0 with "the connection is not enabled" (SYRC-6097).
        connectionName: null,
      },
    };
  }

  if (MOCK_ENTERPRISE_DOMAINS.includes(domain)) {
    return {
      status: SsoLookupStatus.Found,
      preference: {
        ssoEnabled: true,
        provider: SsoProvider.Enterprise,
        connectionName: 'mock-saml-connection',
      },
    };
  }

  // Default: a known account with no SSO configured — password path.
  return {
    status: SsoLookupStatus.Found,
    preference: {
      ssoEnabled: false,
      provider: SsoProvider.None,
      connectionName: null,
    },
  };
}

/**
 * Looks up a user's SSO preference so the login page can decide whether to offer
 * an SSO button or the password path (SYRC-6097, Option B).
 *
 * Currently defaults to a MOCK implementation. Two things are still outstanding
 * before the real endpoint can be called:
 *   1. The per-environment base URL and API key are not yet configured anywhere
 *      (no ticket currently owns adding them).
 *   2. The `sso_provider` encoding is unconfirmed — see sso-preference.model.ts.
 *
 * Flip `environment.ssoPreference.useMock` to false once both are settled.
 *
 * NOTE ON AUTH: this endpoint is called while the user is unauthenticated and is
 * gated only by an `x-api-key` that ships inside the SPA bundle. The key
 * rate-limits, it does not authorize. Keep the endpoint out of `authConfig.secureRoutes`
 * so the OIDC AuthInterceptor never attaches a bearer token to it.
 */
@Injectable({
  providedIn: 'root',
})
export class SsoPreferenceService {
  private readonly http = inject(HttpClient);

  private get config() {
    return environment.ssoPreference;
  }

  /**
   * Resolves the SSO preference for an email address.
   *
   * Never errors: transport and contract problems are folded into
   * {@link SsoLookupStatus.Error} so the caller can degrade gracefully rather
   * than hard-blocking sign-in.
   */
  getPreference(email: string): Observable<SsoLookupResult> {
    const trimmed = email.trim();

    if (!trimmed) {
      return of({ status: SsoLookupStatus.Error } as SsoLookupResult);
    }

    if (this.config.useMock) {
      // The fully-local, hermetic stack (empty configCatSdkKey — the SAME signal
      // createFeatureFlagProvider() uses in app.module.ts) has no real endpoint and
      // no real Auth0 federation to protect: the mock IS the intended data source
      // there, and it is how a developer previews the branded page + SSO button with
      // corinsights_auth0_enabled flipped on locally. Serve the mock directly.
      if (!environment.configCatSdkKey) {
        return this.getMockPreference(trimmed);
      }

      // HARD MISCONFIGURATION GUARD (SYRC-6097 review) — DEPLOYED envs only. The mock
      // is only safe under the legacy STS. If corinsights_auth0_enabled is on,
      // resolveMockPreference() would answer real users with fake data — a
      // @mobiltex.com address gets the bogus 'mock-azure-ad-connection' handed straight
      // to authorize(), and every real enterprise customer falls into the mock's
      // disabled-SSO default so the SSO button never renders. Only QA wires the live
      // endpoint; dev/staging/prod still ship useMock: true, so flipping the flag there
      // hits exactly this. Degrade to Error (never mock) the same way a missing baseUrl
      // already does — non-blocking, so the user still reaches the password path.
      return from(resolveAuth0Enabled()).pipe(
        mergeMap((auth0Enabled) => {
          if (auth0Enabled) {
            console.error(
              '[SsoPreferenceService] useMock is true while Auth0 mode is enabled; the mock ' +
                'must never answer real Auth0 sign-ins. Point this environment at the real ' +
                'SSO-preference endpoint (useMock: false + baseUrl/apiKey) before enabling ' +
                'corinsights_auth0_enabled.',
            );
            return of({ status: SsoLookupStatus.Error } as SsoLookupResult);
          }
          return this.getMockPreference(trimmed);
        }),
      );
    }

    return this.getRemotePreference(trimmed);
  }

  private getMockPreference(email: string): Observable<SsoLookupResult> {
    const delayMs = this.config.mockDelayMs ?? 0;
    // timer(0) still defers by a macrotask, which keeps the loading state
    // observable in the UI and in fakeAsync tests.
    return timer(delayMs).pipe(mergeMap(() => of(resolveMockPreference(email))));
  }

  private getRemotePreference(email: string): Observable<SsoLookupResult> {
    const baseUrl = this.config.baseUrl?.trim();

    if (!baseUrl) {
      // Misconfiguration, not a user problem. Degrade to the graceful-failure
      // path so the user is never stranded.
      console.error(
        '[SsoPreferenceService] useMock is false but ssoPreference.baseUrl is not configured; ' +
          'cannot resolve SSO preference.',
      );
      return of({ status: SsoLookupStatus.Error } as SsoLookupResult);
    }

    const apiKey = this.config.apiKey?.trim();
    const headers = apiKey ? { 'x-api-key': apiKey } : undefined;

    return this.http
      .get<SsoPreferenceResponse>(baseUrl, {
        params: { email },
        ...(headers ? { headers } : {}),
      })
      .pipe(
        timeout(this.config.timeoutMs ?? 8000),
        map((response) => this.toFoundResult(response)),
        catchError((error: unknown) => this.toFailureResult(error)),
      );
  }

  private toFoundResult(response: SsoPreferenceResponse): SsoLookupResult {
    if (typeof response?.sso_enabled !== 'boolean') {
      // Body did not match the contract — treat as a lookup failure rather than
      // guessing at the user's identity provider.
      console.error(
        '[SsoPreferenceService] Unexpected response shape from SSO preference endpoint.',
      );
      return { status: SsoLookupStatus.Error };
    }

    const preference: SsoPreference = normalizeSsoPreference(response);
    return { status: SsoLookupStatus.Found, preference };
  }

  private toFailureResult(error: unknown): Observable<SsoLookupResult> {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      // A 404 is a successful answer: there is no account for this email.
      return of({ status: SsoLookupStatus.NotFound } as SsoLookupResult);
    }

    console.error('[SsoPreferenceService] SSO preference lookup failed.', error);
    return of({ status: SsoLookupStatus.Error } as SsoLookupResult);
  }
}
